import createDebug from 'debug';
import { ChevronLeft, ChevronRight, Loader2, Users } from 'lucide-react';
import { lazy, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
	type LinkResponse,
	type LinkSummaryResponse,
	type QueueResponse,
} from '@/api/client';
import {
	fetchArchivedLinksGQL,
	fetchLinkDetailsGQL,
	fetchQueueLinksGQL,
	useCategoriesQuery,
	useQueuesQuery,
} from '@/hooks/useGraphQLQueries';
import { useAssignQueue, useEnrichLink } from '@/hooks/useLinks';
import { useLinkSubscription, type LinkChangeEvent } from '@/hooks/useLinkSubscription';
import { BulkOperationsPanel } from '@/components/BulkOperationsPanel';
import { CommandPalette } from '@/components/CommandPalette';
import { FilterBar } from '@/components/FilterBar';
import { HealthBanner } from '@/components/HealthBanner';
import { FilterRulesManager } from '@/components/FilterRulesManager';
import { RelatedLinksSidebar } from '@/components/RelatedLinksSidebar';
import { TagSearchModal } from '@/components/TagSearchModal';
import { KeyboardShortcutsOverlay } from '@/components/KeyboardShortcutsOverlay';
import { LinkDetailPanel } from '@/components/LinkDetailPanel';
import { LinkList } from '@/components/LinkList';
import { PageModal } from '@/components/PageModal';
import { PublicAccessManagerDialog } from '@/components/PublicAccessManagerDialog';
import { QueueManagementDialog } from '@/components/QueueManagementDialog';
import { QueueSettingsDialog } from '@/components/QueueSettingsDialog';
import { SubmissionTokenManagerDialog } from '@/components/SubmissionTokenManagerDialog';
import { QueueSelector } from '@/components/QueueSelector';
import { ARCHIVE_QUEUE_ID, QueueSwitcher } from '@/components/QueueSwitcher';
import { QuickAddDialog } from '@/components/QuickAddDialog';
import { UserMenu } from '@/components/UserMenu';
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select';
import { useModalManager } from '@/hooks/useModalManager';
import { useRelatedLinks } from '@/hooks/useRelatedLinks';
import { getCategoryFromTags } from '@/lib/tags';
import { cn } from '@/lib/utils';

const debug = createDebug('app:main');

// Lazy-loaded page modals for code splitting
const MODAL_CONFIG: Record<
	string,
	{
		Component: React.LazyExoticComponent<React.ComponentType>;
		title: string;
		description: string;
	}
> = {
	security: {
		Component: lazy(() => import('@/pages/settings/SecurityPage')),
		title: 'Security Settings',
		description: 'Manage your passwords and authentication methods',
	},
	passkeys: {
		Component: lazy(() => import('@/pages/settings/PasskeysPage')),
		title: 'Manage Passkeys',
		description: 'View and manage your registered passkeys',
	},
	'api-keys': {
		Component: lazy(() => import('@/pages/ApiKeysPage')),
		title: 'API Keys',
		description: 'Manage API keys for programmatic access',
	},
	users: {
		Component: lazy(() => import('@/pages/admin/UserManagementPage')),
		title: 'User Management',
		description: 'Manage users and invitations',
	},
	enrichment: {
		Component: lazy(() => import('@/pages/admin/EnrichmentStatusPage')),
		title: 'Enrichment Status',
		description: 'Monitor progressive enrichment system',
	},
	'reddit-import': {
		Component: lazy(() => import('@/pages/admin/RedditImportPage')),
		title: 'Reddit Import',
		description: 'Monitor Reddit saved posts import job',
	},
	'tag-admin': {
		Component: lazy(() => import('@/pages/admin/TagAdminPage')),
		title: 'Tag Admin',
		description: 'Manage tag properties, auto-download, and display settings',
	},
	profile: {
		Component: lazy(() => import('@/pages/ProfilePage')),
		title: 'My Profile',
		description: 'View and manage your profile information',
	},
	settings: {
		Component: lazy(() => import('@/pages/SettingsPage')),
		title: 'Settings',
		description: 'Configure application preferences',
	},
};

function App() {
	const [categoryFilter, setCategoryFilter] = useState<string>('all');
	const [showNsfw, setShowNsfw] = useState<boolean>(false);
	const [showSnoozed, setShowSnoozed] = useState<boolean>(false);
	const [selectedLinks, setSelectedLinks] = useState<LinkSummaryResponse[]>([]);
	const [showShortcuts, setShowShortcuts] = useState(false);
	const [showQueueSelector, setShowQueueSelector] = useState(false);
	const [showQueueManagement, setShowQueueManagement] = useState(false);
	const [showCommandPalette, setShowCommandPalette] = useState(false);
	const [showQuickAdd, setShowQuickAdd] = useState(false);
	const [showQueueSettings, setShowQueueSettings] = useState(false);
	const [showPublicAccessManager, setShowPublicAccessManager] = useState(false);
	const [showSubmissionTokens, setShowSubmissionTokens] = useState(false);
	const [showFilterRules, setShowFilterRules] = useState(false);
	const [queueSettingsQueue, setQueueSettingsQueue] = useState<QueueResponse | null>(null);

	// Tag-based filtering (CTRL+K tag search)
	const [activeTagFilters, setActiveTagFilters] = useState<string[]>([]);
	const [filterSearchText, setFilterSearchText] = useState('');
	const [showTagSearch, setShowTagSearch] = useState(false);

	// Track last clicked index for shift+click range selection
	const lastClickedIndexRef = useRef<number>(-1);

	// Modal manager
	const { currentModal, openModal, closeModal } = useModalManager();

	// Queue state - use GraphQL for queues and categories (urql cache handles deduplication)
	const { queues, loading: queuesLoading } = useQueuesQuery();
	const { categories, loading: categoriesLoading } = useCategoriesQuery();
	const [currentQueueId, setCurrentQueueId] = useState<number>(0); // 0 = incoming

	// Data state - per-queue caching with slim responses
	const [linksCache, setLinksCache] = useState<Map<number, LinkSummaryResponse[]>>(new Map());
	const [loadedQueues, setLoadedQueues] = useState<Set<number>>(new Set());
	const [archivedLinks, setArchivedLinks] = useState<LinkSummaryResponse[]>([]);
	const [archivedLoaded, setArchivedLoaded] = useState(false);
	const [linksLoading, setLinksLoading] = useState(true); // Initial links load
	const [queueLoading, setQueueLoading] = useState(false); // Loading a specific queue
	const [error, setError] = useState<string | null>(null);

	// Full link details for detail panel (fetched on selection)
	const [selectedLinkDetails, setSelectedLinkDetails] = useState<LinkResponse | null>(null);
	const [, setDetailsLoading] = useState(false);

	// GraphQL mutation hooks
	const assignQueueMutation = useAssignQueue();
	const enrichLinkMutation = useEnrichLink();

	// Handle real-time link change events from GraphQL subscription
	const handleLinkChange = useCallback((event: LinkChangeEvent) => {
		debug('handleLinkChange: action=%s linkId=%d queueId=%d', event.action, event.linkId, event.queueId);

		setLinksCache((cache) => {
			const newCache = new Map(cache);
			let hasChanges = false;

			// For ARCHIVED, DELETED actions - remove from all queues
			if (event.action === 'ARCHIVED' || event.action === 'DELETED') {
				for (const [queueId, links] of cache) {
					const filtered = links.filter((l) => l.id !== event.linkId);
					if (filtered.length !== links.length) {
						newCache.set(queueId, filtered);
						hasChanges = true;
					}
				}
			}
			// For MOVED - remove from previous queue, add to new queue
			else if (event.action === 'MOVED' && event.previousQueueId !== undefined) {
				// Remove from previous queue
				const prevLinks = cache.get(event.previousQueueId);
				if (prevLinks) {
					const filtered = prevLinks.filter((l) => l.id !== event.linkId);
					if (filtered.length !== prevLinks.length) {
						newCache.set(event.previousQueueId, filtered);
						hasChanges = true;
					}
				}
				// Add to new queue if we have the link data and the queue is loaded
				if (event.link && cache.has(event.queueId)) {
					const queueLinks = newCache.get(event.queueId) ?? [];
					// Check if already exists (shouldn't, but be safe)
					if (!queueLinks.some((l) => l.id === event.linkId)) {
						newCache.set(event.queueId, [event.link, ...queueLinks]);
						hasChanges = true;
					}
				}
			}
			// For CREATED, UPDATED, UNARCHIVED - upsert in the target queue
			else if (event.link) {
				const queueLinks = cache.get(event.queueId);
				if (queueLinks) {
					const existing = queueLinks.find((l) => l.id === event.linkId);
					if (!existing || existing.updatedAt !== event.link.updatedAt) {
						const filtered = queueLinks.filter((l) => l.id !== event.linkId);
						// Add at the beginning for new links
						newCache.set(event.queueId, [event.link, ...filtered]);
						hasChanges = true;
					}
				} else if (event.action === 'CREATED') {
					// New link in a queue we haven't loaded yet - just add it if it's the inbox (0)
					if (event.queueId === 0) {
						newCache.set(0, [event.link]);
						hasChanges = true;
					}
				}
			}

			return hasChanges ? newCache : cache;
		});

		// Also update selectedLinkDetails if this is the currently viewed link
		if (event.link && event.action === 'UPDATED') {
			setSelectedLinkDetails((current) => {
				if (!current || current.id !== event.linkId) return current;
				// Merge subscription data into existing details (preserves metadata, enrichment, etc.)
				return {
					...current,
					title: event.link!.title,
					description: event.link!.description,
					url: event.link!.url,
					queueId: event.link!.queueId,
					thumbnailUrl: event.link!.thumbnailUrl,
					isEnriching: event.link!.isEnriching,
					updatedAt: event.link!.updatedAt,
					tags: event.link!.tags,
				};
			});
		}
	}, []);

	// Loading state - combines GraphQL loading for meta (queues/categories) and links loading
	const metaLoading = queuesLoading || categoriesLoading;
	const loading = metaLoading || linksLoading || queueLoading;

	// Load initial links on mount (only incoming queue for fast startup)
	// Note: queues and categories come from GraphQL hooks above
	useEffect(() => {
		async function loadInitialLinks() {
			try {
				debug('loading initial links (includeSnoozed=%s)...', showSnoozed);
				const incomingRes = await fetchQueueLinksGQL(0, { includeSnoozed: showSnoozed });

				debug('loaded: %d links (incoming)', incomingRes.links.length);

				// Initialize cache with incoming queue
				const cache = new Map<number, LinkSummaryResponse[]>();
				cache.set(0, incomingRes.links);
				setLinksCache(cache);
				setLoadedQueues(new Set([0]));

				setLinksLoading(false);
			} catch (err) {
				debug('error loading initial links: %o', err);

				// Check if it's an auth error - redirect to login
				const errorMsg = err instanceof Error ? err.message : String(err);
				const isAuthError =
					errorMsg.includes('401') ||
					errorMsg.includes('Authentication required') ||
					errorMsg.includes('Unauthorized') ||
					errorMsg.includes('Not authenticated');

				if (isAuthError) {
					debug('auth error detected, redirecting to login');
					window.location.href = '/login';
					return;
				}

				setError(errorMsg);
				setLinksLoading(false);
			}
		}
		loadInitialLinks();
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []); // Initial load only - showSnoozed changes handled by dedicated effect

	// Load queue links when switching to a new queue
	useEffect(() => {
		// Skip if already loaded or it's archive
		if (currentQueueId === ARCHIVE_QUEUE_ID) return;
		if (loadedQueues.has(currentQueueId)) return;
		if (linksLoading) return; // Wait for initial links load to complete

		async function loadQueue() {
			try {
				setQueueLoading(true);
				debug('loading queue %d (includeSnoozed=%s)...', currentQueueId, showSnoozed);
				const res = await fetchQueueLinksGQL(currentQueueId, { includeSnoozed: showSnoozed });
				debug('loaded %d links for queue %d', res.links.length, currentQueueId);

				setLinksCache((cache) => {
					const newCache = new Map(cache);
					newCache.set(currentQueueId, res.links);
					return newCache;
				});
				setLoadedQueues((loaded) => new Set([...loaded, currentQueueId]));
			} catch (err) {
				debug('error loading queue %d: %o', currentQueueId, err);
			} finally {
				setQueueLoading(false);
			}
		}
		loadQueue();
	}, [currentQueueId, loadedQueues, linksLoading, showSnoozed]);

	// Refetch current queue when showSnoozed toggle changes
	const showSnoozedRef = useRef(showSnoozed);
	useEffect(() => {
		// Skip on initial mount
		if (showSnoozedRef.current === showSnoozed) return;
		showSnoozedRef.current = showSnoozed;

		// Clear loadedQueues cache so other queues get refetched with new snoozed setting
		setLoadedQueues(new Set([currentQueueId]));

		// Don't refetch if we're viewing archive (no snoozed links there)
		if (currentQueueId === ARCHIVE_QUEUE_ID) return;

		async function refetchCurrentQueue() {
			try {
				setQueueLoading(true);
				debug('refetching queue %d due to showSnoozed change (includeSnoozed=%s)...', currentQueueId, showSnoozed);
				const res = await fetchQueueLinksGQL(currentQueueId, { includeSnoozed: showSnoozed });
				debug('refetched %d links for queue %d', res.links.length, currentQueueId);

				setLinksCache((cache) => {
					const newCache = new Map(cache);
					newCache.set(currentQueueId, res.links);
					return newCache;
				});
			} catch (err) {
				debug('error refetching queue %d: %o', currentQueueId, err);
			} finally {
				setQueueLoading(false);
			}
		}
		refetchCurrentQueue();
	}, [showSnoozed, currentQueueId]);

	// Lazy-load archived links when Archive queue is selected
	useEffect(() => {
		if (currentQueueId !== ARCHIVE_QUEUE_ID || archivedLoaded) return;

		async function loadArchived() {
			try {
				setQueueLoading(true);
				debug('loading archived links...');
				const archivedRes = await fetchArchivedLinksGQL();
				debug('loaded %d archived links', archivedRes.links.length);
				// GraphQL returns LinkSummaryResponse directly, no conversion needed
				setArchivedLinks(archivedRes.links);
				setArchivedLoaded(true);
			} catch (err) {
				debug('error loading archived: %o', err);
			} finally {
				setQueueLoading(false);
			}
		}
		loadArchived();
	}, [currentQueueId, archivedLoaded]);

	// Subscribe to real-time link changes via GraphQL WebSocket
	const { connected: subscriptionConnected, error: subscriptionError } = useLinkSubscription({
		onLinkChange: handleLinkChange,
		enabled: !metaLoading,
	});

	// Log subscription status in debug mode
	useEffect(() => {
		if (subscriptionError) {
			debug('subscription error: %o', subscriptionError);
		} else if (subscriptionConnected) {
			debug('subscription connected');
		}
	}, [subscriptionConnected, subscriptionError]);

	// Build set of NSFW category IDs for quick lookup (memoized)
	const nsfwCategories = useMemo(
		() => new Set(categories.filter((c) => c.nsfw).map((c) => c.id)),
		[categories]
	);

	// Get current queue's links from cache
	const currentQueueLinks = useMemo(() => {
		if (currentQueueId === ARCHIVE_QUEUE_ID) {
			return archivedLinks;
		}
		return linksCache.get(currentQueueId) ?? [];
	}, [currentQueueId, linksCache, archivedLinks]);

	// Filter links by category, NSFW, tags, and search text (memoized)
	const filteredLinks = useMemo(() => {
		let result = currentQueueLinks;

		// NSFW filter
		if (!showNsfw) {
			result = result.filter((link) => {
				const linkCategory = getCategoryFromTags(link.tags);
				return !linkCategory || !nsfwCategories.has(linkCategory);
			});
		}

		// Category filter
		if (categoryFilter !== 'all') {
			result = result.filter((link) => getCategoryFromTags(link.tags) === categoryFilter);
		}

		// Tag filters (AND logic - must have ALL selected tags)
		if (activeTagFilters.length > 0) {
			result = result.filter((link) => {
				const linkTagNames = new Set(link.tags?.map((t) => t.name) ?? []);
				return activeTagFilters.every((tagName) => linkTagNames.has(tagName));
			});
		}

		// Free-text search (title, description, url)
		if (filterSearchText.trim()) {
			const lower = filterSearchText.toLowerCase();
			result = result.filter(
				(link) =>
					link.title.toLowerCase().includes(lower) ||
					link.description?.toLowerCase().includes(lower) ||
					link.url.toLowerCase().includes(lower)
			);
		}

		return result;
	}, [
		currentQueueLinks,
		showNsfw,
		nsfwCategories,
		categoryFilter,
		activeTagFilters,
		filterSearchText,
	]);

	// Compute link counts per queue (memoized)
	const linkCounts = useMemo(() => {
		const counts = new Map<number, number>();
		for (const [queueId, queueLinks] of linksCache.entries()) {
			let count = 0;
			for (const link of queueLinks) {
				const category = getCategoryFromTags(link.tags);
				if (!showNsfw && category && nsfwCategories.has(category)) continue;
				count++;
			}
			counts.set(queueId, count);
		}
		return counts;
	}, [linksCache, showNsfw, nsfwCategories]);

	// Compute archived link count (respects NSFW filter)
	const archivedCount = useMemo(() => {
		let count = 0;
		for (const link of archivedLinks) {
			const category = getCategoryFromTags(link.tags);
			if (!showNsfw && category && nsfwCategories.has(category)) continue;
			count++;
		}
		return count;
	}, [archivedLinks, showNsfw, nsfwCategories]);

	// Filter categories shown in dropdown (hide NSFW categories when showNsfw is false)
	const visibleCategories = useMemo(
		() => (showNsfw ? categories : categories.filter((c) => !c.nsfw)),
		[showNsfw, categories]
	);

	// Derive "selected link" for single-selection mode (for detail panel and navigation)
	const selectedLink = selectedLinks.length === 1 ? selectedLinks[0] : null;

	// Related links sidebar - collapsed by default, only queries when expanded
	const [showRelatedSidebar, setShowRelatedSidebar] = useState(false);

	// Fetch full details when a single link is selected (for detail panel)
	useEffect(() => {
		if (!selectedLink) {
			setSelectedLinkDetails(null);
			return;
		}

		// Collapse related sidebar when switching links
		setShowRelatedSidebar(false);

		// Check if we already have the details cached
		if (selectedLinkDetails?.id === selectedLink.id) return;

		async function fetchDetails() {
			try {
				setDetailsLoading(true);
				const details = await fetchLinkDetailsGQL(selectedLink!.id);
				setSelectedLinkDetails(details);
			} catch (err) {
				debug('error fetching link details: %o', err);
				// Fall back to slim data - create a partial LinkResponse
				setSelectedLinkDetails({
					...selectedLink!,
					metadata: null,
					source: null,
					createdAt: selectedLink!.updatedAt,
					archivedAt: null,
				} as LinkResponse);
			} finally {
				setDetailsLoading(false);
			}
		}
		fetchDetails();
	}, [selectedLink?.id]); // Only re-fetch when selected ID changes

	// Related links query - paused until sidebar is expanded
	const { data: relatedData } = useRelatedLinks(
		selectedLink?.id ?? null,
		selectedLink?.queueId ?? 0,
		!showRelatedSidebar // pause when collapsed
	);
	const hasRelatedLinks = relatedData && relatedData.total > 0;

	// Set of selected IDs for efficient lookups
	const selectedLinkIds = useMemo(() => new Set(selectedLinks.map((l) => l.id)), [selectedLinks]);

	// Calculate current index and navigation state
	const currentIndex = useMemo(() => {
		if (!selectedLink) return -1;
		return filteredLinks.findIndex((l) => l.id === selectedLink.id);
	}, [selectedLink, filteredLinks]);

	const hasNext = currentIndex >= 0 && currentIndex < filteredLinks.length - 1;
	const hasPrevious = currentIndex > 0;

	// Handle tag click - add to active filters
	const handleTagClick = useCallback((tag: { name: string }) => {
		setActiveTagFilters((prev) => {
			if (prev.includes(tag.name)) return prev; // Already active
			return [...prev, tag.name];
		});
	}, []);


	// Handle card click with multi-select support
	const handleCardClick = useCallback(
		(link: LinkSummaryResponse, event: React.MouseEvent) => {
			const clickedIndex = filteredLinks.findIndex((l) => l.id === link.id);

			if (event.shiftKey && lastClickedIndexRef.current >= 0) {
				// Shift+click: range selection
				const start = Math.min(lastClickedIndexRef.current, clickedIndex);
				const end = Math.max(lastClickedIndexRef.current, clickedIndex);
				const rangeLinks = filteredLinks.slice(start, end + 1);

				if (event.metaKey || event.ctrlKey) {
					// Shift+Cmd: add range to existing selection
					setSelectedLinks((prev) => {
						const existingIds = new Set(prev.map((l) => l.id));
						const newLinks = rangeLinks.filter((l) => !existingIds.has(l.id));
						return [...prev, ...newLinks];
					});
				} else {
					// Shift only: replace selection with range
					setSelectedLinks(rangeLinks);
				}
			} else if (event.metaKey || event.ctrlKey) {
				// Cmd+click: toggle selection
				setSelectedLinks((prev) => {
					const isSelected = prev.some((l) => l.id === link.id);
					if (isSelected) {
						return prev.filter((l) => l.id !== link.id);
					}
					return [...prev, link];
				});
				lastClickedIndexRef.current = clickedIndex;
			} else {
				// Plain click: single selection
				setSelectedLinks([link]);
				lastClickedIndexRef.current = clickedIndex;
			}
		},
		[filteredLinks]
	);

	// Clear selection
	const handleClearSelection = useCallback(() => {
		setSelectedLinks([]);
		lastClickedIndexRef.current = -1;
	}, []);

	// Navigation - navigate through filtered links
	const handleNextLink = useCallback(() => {
		if (!selectedLink) {
			// If nothing selected, select first
			if (filteredLinks.length > 0) {
				setSelectedLinks([filteredLinks[0]]);
				lastClickedIndexRef.current = 0;
			}
			return;
		}
		const idx = filteredLinks.findIndex((l) => l.id === selectedLink.id);
		if (idx < filteredLinks.length - 1) {
			setSelectedLinks([filteredLinks[idx + 1]]);
			lastClickedIndexRef.current = idx + 1;
		}
	}, [selectedLink, filteredLinks]);

	const handlePreviousLink = useCallback(() => {
		if (!selectedLink) return;
		const idx = filteredLinks.findIndex((l) => l.id === selectedLink.id);
		if (idx > 0) {
			setSelectedLinks([filteredLinks[idx - 1]]);
			lastClickedIndexRef.current = idx - 1;
		}
	}, [selectedLink, filteredLinks]);

	// Handle link updates - sync to local state and cache
	const handleLinkUpdated = useCallback((updatedLink: LinkResponse) => {
		// Convert to slim response for cache
		const slimLink: LinkSummaryResponse = {
			id: updatedLink.id,
			url: updatedLink.url,
			title: updatedLink.title,
			description: updatedLink.description,
			queueId: updatedLink.queueId,
			thumbnailUrl: updatedLink.thumbnailUrl,
			isEnriching: updatedLink.isEnriching,
			updatedAt: updatedLink.updatedAt,
			tags: updatedLink.tags,
		};

		// Update cache
		setLinksCache((cache) => {
			const queueLinks = cache.get(updatedLink.queueId);
			if (!queueLinks) return cache;
			const newCache = new Map(cache);
			newCache.set(
				updatedLink.queueId,
				queueLinks.map((l) => (l.id === updatedLink.id ? slimLink : l))
			);
			return newCache;
		});

		// Update selected link details
		setSelectedLinkDetails(updatedLink);
		setSelectedLinks([slimLink]);
	}, []);

	// Handle multiple links updated (from bulk operations)
	const handleBulkLinksUpdated = useCallback((updatedLinks: LinkSummaryResponse[]) => {
		const updatedMap = new Map(updatedLinks.map((l) => [l.id, l]));

		// Update cache for all affected queues
		setLinksCache((cache) => {
			const newCache = new Map(cache);
			for (const [queueId, queueLinks] of cache.entries()) {
				const updated = queueLinks.map((l) => {
					const full = updatedMap.get(l.id);
					if (!full) return l;
					return {
						id: full.id,
						url: full.url,
						title: full.title,
						description: full.description,
						queueId: full.queueId,
						thumbnailUrl: full.thumbnailUrl,
						isEnriching: full.isEnriching,
						updatedAt: full.updatedAt,
						tags: full.tags,
					};
				});
				newCache.set(queueId, updated);
			}
			return newCache;
		});

		// Update selected links with new data
		setSelectedLinks((prev) =>
			prev.map((l) => {
				const full = updatedMap.get(l.id);
				if (!full) return l;
				return {
					id: full.id,
					url: full.url,
					title: full.title,
					description: full.description,
					queueId: full.queueId,
					thumbnailUrl: full.thumbnailUrl,
					isEnriching: full.isEnriching,
					updatedAt: full.updatedAt,
					tags: full.tags,
				};
			})
		);
	}, []);

	// Handle multiple links archived (from bulk operations)
	const handleBulkLinksArchived = useCallback((archivedIds: number[]) => {
		const archivedSet = new Set(archivedIds);

		// Remove from all queue caches and add to archived
		setLinksCache((cache) => {
			const newCache = new Map(cache);
			const toArchive: LinkSummaryResponse[] = [];

			for (const [queueId, queueLinks] of cache.entries()) {
				const archived = queueLinks.filter((l) => archivedSet.has(l.id));
				toArchive.push(...archived);
				newCache.set(
					queueId,
					queueLinks.filter((l) => !archivedSet.has(l.id))
				);
			}

			// Add to archived list
			if (toArchive.length > 0) {
				setArchivedLinks((prev) => [...toArchive, ...prev]);
			}

			return newCache;
		});

		// Remove archived links from selection
		setSelectedLinks((prev) => prev.filter((l) => !archivedSet.has(l.id)));
		lastClickedIndexRef.current = -1;
	}, []);

	// Handle link archived - move to archived state and select next/previous
	const handleLinkArchived = useCallback(
		(linkId: number) => {
			// Find next link to select before updating state
			const idx = filteredLinks.findIndex((l) => l.id === linkId);
			let nextSelection: LinkSummaryResponse | null = null;
			if (idx < filteredLinks.length - 1) {
				nextSelection = filteredLinks[idx + 1];
			} else if (idx > 0) {
				nextSelection = filteredLinks[idx - 1];
			}

			// Remove from current queue cache and add to archived
			setLinksCache((cache) => {
				const newCache = new Map(cache);
				for (const [queueId, queueLinks] of cache.entries()) {
					const link = queueLinks.find((l) => l.id === linkId);
					if (link) {
						setArchivedLinks((prev) => [link, ...prev]);
						newCache.set(
							queueId,
							queueLinks.filter((l) => l.id !== linkId)
						);
						break;
					}
				}
				return newCache;
			});

			setSelectedLinks(nextSelection ? [nextSelection] : []);
			lastClickedIndexRef.current = nextSelection
				? filteredLinks.findIndex((l) => l.id === nextSelection!.id)
				: -1;
		},
		[filteredLinks]
	);

	// Handle link unarchived - move back to active state
	const handleLinkUnarchived = useCallback(
		(linkId: number) => {
			// Find next link to select before updating state (similar to archive)
			const idx = filteredLinks.findIndex((l) => l.id === linkId);
			let nextSelection: LinkSummaryResponse | null = null;
			if (idx < filteredLinks.length - 1) {
				nextSelection = filteredLinks[idx + 1];
			} else if (idx > 0) {
				nextSelection = filteredLinks[idx - 1];
			}

			// Move link from archived to its queue cache
			setArchivedLinks((prev) => {
				const link = prev.find((l) => l.id === linkId);
				if (link) {
					setLinksCache((cache) => {
						const queueId = link.queueId;
						const queueLinks = cache.get(queueId) ?? [];
						const newCache = new Map(cache);
						newCache.set(queueId, [link, ...queueLinks]);
						return newCache;
					});
				}
				return prev.filter((l) => l.id !== linkId);
			});

			setSelectedLinks(nextSelection ? [nextSelection] : []);
			lastClickedIndexRef.current = nextSelection
				? filteredLinks.findIndex((l) => l.id === nextSelection!.id)
				: -1;
		},
		[filteredLinks]
	);

	// Handle queue assignment from QueueSelector
	const handleQueueAssign = useCallback(
		async (queueId: number) => {
			if (!selectedLink) return;

			const oldQueueId = selectedLink.queueId;
			const updatedLink = { ...selectedLink, queueId };

			// Optimistic update - move between queue caches
			setLinksCache((cache) => {
				const newCache = new Map(cache);

				// Remove from old queue
				const oldQueueLinks = cache.get(oldQueueId) ?? [];
				newCache.set(
					oldQueueId,
					oldQueueLinks.filter((l) => l.id !== selectedLink.id)
				);

				// Add to new queue (if loaded)
				if (cache.has(queueId)) {
					const newQueueLinks = cache.get(queueId) ?? [];
					newCache.set(queueId, [updatedLink, ...newQueueLinks]);
				}

				return newCache;
			});

			setSelectedLinks([updatedLink]);

			// Fire GraphQL mutation
			try {
				await assignQueueMutation.mutateAsync({ id: selectedLink.id, queueId });
			} catch (err) {
				debug('assignQueue failed: %o', err);
			}
		},
		[selectedLink, assignQueueMutation]
	);

	// Navigate to a specific link (used by QuickAddDialog for duplicates)
	const handleNavigateToLink = useCallback(
		(linkId: number, queueId: number, isArchived: boolean) => {
			// Switch to the appropriate queue
			if (isArchived) {
				setCurrentQueueId(ARCHIVE_QUEUE_ID);
				// Find in archived links
				const targetLink = archivedLinks.find((l) => l.id === linkId);
				if (targetLink) {
					setSelectedLinks([targetLink]);
				}
			} else {
				setCurrentQueueId(queueId);
				// Find in queue cache
				const queueLinks = linksCache.get(queueId);
				const targetLink = queueLinks?.find((l) => l.id === linkId);
				if (targetLink) {
					setSelectedLinks([targetLink]);
				}
			}
		},
		[linksCache, archivedLinks]
	);

	// Handle enriching the selected link
	const handleEnrichSelected = useCallback(async () => {
		if (!selectedLink) return;

		try {
			debug('enriching link via GraphQL: %d', selectedLink.id);
			await enrichLinkMutation.mutateAsync({ id: selectedLink.id });
			// Note: The subscription will handle updating the UI when enrichment completes
		} catch (err) {
			debug('enrich failed: %o', err);
		}
	}, [selectedLink, enrichLinkMutation]);

	// Tag filter handlers
	const handleAddTagFilter = useCallback((tagName: string) => {
		setActiveTagFilters((prev) => {
			if (prev.includes(tagName)) return prev; // Already active
			return [...prev, tagName];
		});
	}, []);

	const handleRemoveTagFilter = useCallback((tagName: string) => {
		setActiveTagFilters((prev) => prev.filter((t) => t !== tagName));
	}, []);

	const handleClearAllFilters = useCallback(() => {
		setActiveTagFilters([]);
		setFilterSearchText('');
	}, []);

	// Global keyboard shortcuts
	useEffect(() => {
		function handleGlobalKeyDown(e: KeyboardEvent) {
			// Skip if user is typing in an input
			const isEditing =
				e.target instanceof HTMLInputElement ||
				e.target instanceof HTMLTextAreaElement ||
				(e.target instanceof HTMLElement && e.target.isContentEditable);

			// Command palette: CMD+P or CMD+. (works even when editing)
			if ((e.metaKey || e.ctrlKey) && (e.key === 'p' || e.key === '.')) {
				e.preventDefault();
				setShowCommandPalette(true);
				return;
			}

			// Quick add: CMD+N (works even when editing)
			if ((e.metaKey || e.ctrlKey) && e.key === 'n') {
				e.preventDefault();
				setShowQuickAdd(true);
				return;
			}

			// Tag search: CMD+K (works even when editing)
			if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
				e.preventDefault();
				setShowTagSearch(true);
				return;
			}

			if (isEditing) return;

			// ? key (with shift) to show shortcuts
			if (e.key === '?' || (e.shiftKey && e.key === '/')) {
				e.preventDefault();
				setShowShortcuts(true);
				return;
			}

			// Cmd+A: select all visible links
			if ((e.metaKey || e.ctrlKey) && e.key === 'a') {
				e.preventDefault();
				setSelectedLinks(filteredLinks);
				if (filteredLinks.length > 0) {
					lastClickedIndexRef.current = filteredLinks.length - 1;
				}
				return;
			}

			// Escape: clear selection
			if (e.key === 'Escape') {
				e.preventDefault();
				setSelectedLinks([]);
				lastClickedIndexRef.current = -1;
				return;
			}
		}

		// Listen for custom events from CommandPalette
		function handleOpenQueueSelector() {
			setShowQueueSelector(true);
		}
		function handleOpenFilterRules() {
			setShowFilterRules(true);
		}
		function handleOpenTagAdmin() {
			openModal('tag-admin');
		}

		window.addEventListener('keydown', handleGlobalKeyDown);
		document.addEventListener('open-queue-selector', handleOpenQueueSelector);
		document.addEventListener('open-filter-rules', handleOpenFilterRules);
		document.addEventListener('open-tag-admin', handleOpenTagAdmin);
		return () => {
			window.removeEventListener('keydown', handleGlobalKeyDown);
			document.removeEventListener('open-queue-selector', handleOpenQueueSelector);
			document.removeEventListener('open-filter-rules', handleOpenFilterRules);
			document.removeEventListener('open-tag-admin', handleOpenTagAdmin);
		};
	}, [filteredLinks]);

	if (loading) {
		return (
			<div className="flex h-screen items-center justify-center bg-background">
				<Loader2 className="size-8 animate-spin text-muted-foreground" />
			</div>
		);
	}

	if (error) {
		return (
			<div className="flex h-screen items-center justify-center bg-background">
				<div className="text-center max-w-md px-4">
					<p className="text-destructive text-lg font-semibold">Failed to load links</p>
					<p className="text-muted-foreground text-sm mt-2">{error}</p>
					<div className="mt-4 flex gap-2 justify-center">
						<button
							type="button"
							onClick={() => window.location.reload()}
							className="px-4 py-2 text-sm bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
						>
							Retry
						</button>
						<button
							type="button"
							onClick={() => (window.location.href = '/login')}
							className="px-4 py-2 text-sm border border-input rounded-md hover:bg-accent"
						>
							Go to Login
						</button>
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className="flex h-screen flex-col bg-background text-foreground">
			{/* Header */}
			<header className="flex items-center justify-between border-b border-border px-4 py-2 shrink-0 bg-background">
				<div className="flex items-center gap-3">
					{/* Terminal Prompt Logo */}
					<div className="flex items-center gap-0.5 font-mono text-lg">
						<span className="text-muted-foreground">&gt;</span>
						<span className="text-primary font-bold tracking-tight">LINKS</span>
						<span className="text-primary animate-cursor-blink">_</span>
					</div>
					<QueueSwitcher
						queues={queues}
						currentQueueId={currentQueueId}
						onQueueChange={setCurrentQueueId}
						onManageQueues={() => setShowQueueManagement(true)}
						onQueueSettings={(queue) => {
							setQueueSettingsQueue(queue);
							setShowQueueSettings(true);
						}}
						linkCounts={linkCounts}
						archivedCount={archivedCount}
						showSnoozed={showSnoozed}
						onShowSnoozedChange={setShowSnoozed}
					/>
					<button
						type="button"
						onClick={() => setShowQuickAdd(true)}
						className="flex items-center justify-center h-8 px-2 text-muted-foreground bg-transparent hover:text-primary border border-border hover:border-primary transition-colors font-mono text-sm"
						title="Quick Add (⌘N)"
					>
						[+]
					</button>
					<button
						type="button"
						onClick={() => setShowTagSearch(true)}
						className="hidden md:flex items-center gap-2 px-3 py-1.5 text-sm text-muted-foreground bg-transparent hover:text-primary border border-border hover:border-primary transition-colors font-mono"
					>
						<span className="text-muted-foreground">/</span>
						<span>search tags</span>
						<kbd className="ml-2 pointer-events-none hidden h-5 select-none items-center gap-1 border border-border bg-background px-1.5 font-mono text-[10px] font-medium opacity-100 sm:flex">
							<span className="text-xs">&#8984;</span>K
						</kbd>
					</button>
				</div>
				<div className="flex items-center gap-3">
					<label className="hidden sm:flex items-center gap-2 cursor-pointer select-none">
						<input
							type="checkbox"
							checked={showNsfw}
							onChange={(e) => setShowNsfw(e.target.checked)}
							className="size-4 rounded border-gray-300 text-pink-600 focus:ring-pink-500"
						/>
						<span className="text-sm text-muted-foreground">NSFW</span>
					</label>
					<Select value={categoryFilter} onValueChange={setCategoryFilter}>
						<SelectTrigger className="w-40 sm:w-48">
							<SelectValue placeholder="Filter by category" />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value="all">All ({filteredLinks.length})</SelectItem>
							{visibleCategories.map((cat) => {
								const count = filteredLinks.filter(
									(link) => getCategoryFromTags(link.tags) === cat.id
								).length;
								return (
									<SelectItem key={cat.id} value={cat.id}>
										{cat.name} ({count})
									</SelectItem>
								);
							})}
						</SelectContent>
					</Select>
					<UserMenu
						onOpenPublicAccess={() => setShowPublicAccessManager(true)}
						onOpenSubmissionTokens={() => setShowSubmissionTokens(true)}
					/>
				</div>
			</header>

			{/* Health Banner - high/critical flags only */}
			<HealthBanner />

			{/* Filter Bar - always visible */}
			<FilterBar
				activeTagFilters={activeTagFilters}
				onRemoveTagFilter={handleRemoveTagFilter}
				onClearAllFilters={handleClearAllFilters}
				searchText={filterSearchText}
				onSearchTextChange={setFilterSearchText}
				onOpenTagSearch={() => setShowTagSearch(true)}
				resultCount={filteredLinks.length}
			/>

			{/* Main split layout: vertical on mobile, horizontal on desktop */}
			<main className="flex-1 flex flex-col lg:flex-row overflow-hidden min-w-0">
				{/* Link List - left side on desktop, top on mobile */}
				<div className="flex-1 lg:flex-none lg:w-[400px] xl:w-[480px] overflow-hidden border-b lg:border-b-0 lg:border-r min-w-0">
					<LinkList
						links={filteredLinks}
						onCardClick={handleCardClick}
						onTagClick={handleTagClick}
						selectedLinkIds={selectedLinkIds}
						className="h-full"
					/>
				</div>

				{/* Detail Panel - shrinks when related sidebar is shown */}
				<div className={cn(
					"flex-1 min-h-[300px] lg:min-h-0 overflow-hidden",
					hasRelatedLinks && "lg:flex-initial"
				)}>
					{selectedLinks.length > 1 ? (
						<BulkOperationsPanel
							selectedLinks={selectedLinks}
							queues={queues}
							categories={categories}
							onLinksUpdated={handleBulkLinksUpdated}
							onLinksArchived={handleBulkLinksArchived}
							onClearSelection={handleClearSelection}
							className="h-full"
						/>
					) : (
						<LinkDetailPanel
							link={selectedLinkDetails}
							queues={queues}
							onLinkUpdated={handleLinkUpdated}
							onLinkArchived={handleLinkArchived}
							onLinkUnarchived={handleLinkUnarchived}
							onTagClick={handleTagClick}
							onNext={handleNextLink}
							onPrevious={handlePreviousLink}
							onShowShortcuts={() => setShowShortcuts(true)}
							onQueueChange={() => setShowQueueSelector(true)}
							hasNext={hasNext}
							hasPrevious={hasPrevious}
							className="h-full"
						/>
					)}
				</div>

				{/* Related Links Sidebar - collapsed by default, toggle to expand */}
				{selectedLinkDetails && (
					<>
						{/* Collapsed toggle button */}
						{!showRelatedSidebar && (
							<button
								onClick={() => setShowRelatedSidebar(true)}
								className="hidden lg:flex items-center border-l px-1.5 hover:bg-muted/50 transition-colors shrink-0"
								title="Show related links"
							>
								<Users className="size-4 text-muted-foreground" />
								<ChevronLeft className="size-3 text-muted-foreground" />
							</button>
						)}

						{/* Expanded sidebar */}
						{showRelatedSidebar && hasRelatedLinks && (
							<RelatedLinksSidebar
								currentLink={selectedLinkDetails}
								relatedLinks={relatedData!.relatedLinks}
								matchingTags={relatedData!.sourceLink.userTags}
								onArchiveComplete={(archivedIds) => {
									handleBulkLinksArchived(archivedIds);
									if (archivedIds.includes(selectedLinkDetails.id)) {
										handleNextLink();
									}
								}}
								onNavigate={(linkId) => {
									const queueId = selectedLinkDetails.queueId ?? currentQueueId;
									handleNavigateToLink(linkId, queueId, false);
								}}
								onClose={() => setShowRelatedSidebar(false)}
								className="hidden lg:flex lg:w-[320px] xl:w-[380px] border-l shrink-0"
							/>
						)}

						{/* Expanded but no related links found */}
						{showRelatedSidebar && !hasRelatedLinks && (
							<div className="hidden lg:flex items-center border-l px-3 shrink-0">
								<button
									onClick={() => setShowRelatedSidebar(false)}
									className="text-xs text-muted-foreground hover:text-foreground"
								>
									No related links <ChevronRight className="inline size-3" />
								</button>
							</div>
						)}
					</>
				)}
			</main>

			{/* Queue Selector Modal */}
			<QueueSelector
				open={showQueueSelector}
				onOpenChange={setShowQueueSelector}
				queues={queues}
				currentQueueId={selectedLink?.queueId}
				onSelect={handleQueueAssign}
			/>

			{/* Tag Search Modal (CMD+K) */}
			<TagSearchModal
				open={showTagSearch}
				onOpenChange={setShowTagSearch}
				onSelectTag={handleAddTagFilter}
				activeTagFilters={activeTagFilters}
			/>

			
			{/* Command Palette (CMD+P) */}
			<CommandPalette
				open={showCommandPalette}
				onOpenChange={setShowCommandPalette}
				queues={queues}
				selectedLink={selectedLink}
				showNsfw={showNsfw}
				onNavigateToQueue={setCurrentQueueId}
				onToggleNsfw={() => setShowNsfw((prev) => !prev)}
				onArchiveSelected={() => {
					if (selectedLink) handleLinkArchived(selectedLink.id);
				}}
				onOpenQuickAdd={() => setShowQuickAdd(true)}
				onOpenCategorySelector={() => {
					// TODO: implement category selector from command palette
				}}
				onOpenOriginalUrl={() => {
					if (selectedLink) window.open(selectedLink.url, '_blank');
				}}
				onEnrichSelected={handleEnrichSelected}
				onShowShortcuts={() => setShowShortcuts(true)}
			/>

			{/* Quick Add Dialog (CMD+N) */}
			<QuickAddDialog
				open={showQuickAdd}
				onOpenChange={setShowQuickAdd}
				onNavigateToLink={handleNavigateToLink}
			/>

			{/* Keyboard Shortcuts Help */}
			<KeyboardShortcutsOverlay open={showShortcuts} onOpenChange={setShowShortcuts} />

			{/* Queue Management Dialog */}
			<QueueManagementDialog
				open={showQueueManagement}
				onOpenChange={setShowQueueManagement}
				queues={queues}
				onQueuesChanged={() => {
					// No-op: urql's graphcache handles cache invalidation on mutations
					// The useQueuesQuery hook will automatically refetch and re-render
				}}
			/>

			{/* Queue Settings Dialog */}
			<QueueSettingsDialog
				open={showQueueSettings}
				onOpenChange={(open) => {
					setShowQueueSettings(open);
					if (!open) setQueueSettingsQueue(null);
				}}
				queue={queueSettingsQueue}
				onBack={() => {
					setShowQueueSettings(false);
					setQueueSettingsQueue(null);
					setShowPublicAccessManager(true);
				}}
			/>

			{/* Public Access Manager Dialog */}
			<PublicAccessManagerDialog
				open={showPublicAccessManager}
				onOpenChange={setShowPublicAccessManager}
				queues={queues}
				onQueueSettings={(queue) => {
					setQueueSettingsQueue(queue);
					setShowQueueSettings(true);
					setShowPublicAccessManager(false);
				}}
			/>

			{/* Submission Token Manager Dialog */}
			<SubmissionTokenManagerDialog
				open={showSubmissionTokens}
				onOpenChange={setShowSubmissionTokens}
			/>

			{/* Filter Rules Manager Dialog */}
			<FilterRulesManager
				open={showFilterRules}
				onOpenChange={setShowFilterRules}
			/>

			{/* Page Modals (Lazy-loaded) */}
			{currentModal && MODAL_CONFIG[currentModal] && (() => {
				const { Component, title, description } = MODAL_CONFIG[currentModal];
				return (
					<PageModal open={true} onOpenChange={closeModal} title={title} description={description}>
						<Component />
					</PageModal>
				);
			})()}
		</div>
	);
}

export default App;
